/**
 * KayaSparsha Chatbot
 *
 * A floating chat widget that replaces the old "link to WhatsApp" float.
 * Walks a visitor through a booking conversation (name → phone → service →
 * date → time → confirm), POSTs to /api/public/appointments, and relies on
 * the backend to fire a WhatsApp confirmation via Meta Cloud API.
 *
 * Design goals:
 * - No external chatbot library, no localStorage (we keep it simple and
 *   respect the app's no-storage rule).
 * - Service list is fetched live from /api/public/services so it always
 *   matches what the clinic software has.
 * - Date & time are picker-driven (never free-text) so there's no chance
 *   of mis-parsed formats like "19-04-2026" vs "04/19/2026".
 * - Gracefully degrades if the WhatsApp sender isn't configured — the
 *   booking is still stored and the user is told staff will follow up.
 */

import React, { useEffect, useRef, useState } from 'react';
import './chatbot.css';

const STEPS = {
  GREETING: 'greeting',
  NAME: 'name',
  PHONE: 'phone',
  SERVICE: 'service',
  DATE: 'date',
  TIME: 'time',
  CONFIRM: 'confirm',
  SUBMITTING: 'submitting',
  DONE: 'done',
  ERROR: 'error',
};

// Clinic hours — tweak to match the real schedule
const TIME_SLOTS = [
  { value: '09:00', label: '9:00 AM' },
  { value: '10:00', label: '10:00 AM' },
  { value: '11:00', label: '11:00 AM' },
  { value: '12:00', label: '12:00 PM' },
  { value: '15:00', label: '3:00 PM' },
  { value: '16:00', label: '4:00 PM' },
  { value: '17:00', label: '5:00 PM' },
  { value: '18:00', label: '6:00 PM' },
];

// ---------- Date helpers ----------

const pad2 = (n) => String(n).padStart(2, '0');

const toIso = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

const todayIso = () => toIso(new Date());

const addDays = (d, n) => {
  const c = new Date(d);
  c.setDate(c.getDate() + n);
  return c;
};

const formatDateLabel = (iso) => {
  if (!iso) return '';
  // Append local midday so timezone math doesn't shift the day
  const d = new Date(`${iso}T12:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, {
    weekday: 'short', day: '2-digit', month: 'short', year: 'numeric',
  });
};

const getQuickDates = () => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const dow = today.getDay(); // 0=Sun, 1=Mon...
  const daysUntilNextMon = ((8 - dow) % 7) || 7;

  const items = [
    { iso: toIso(today),              label: 'Today' },
    { iso: toIso(addDays(today, 1)),  label: 'Tomorrow' },
    { iso: toIso(addDays(today, 2)),  label: formatDateLabel(toIso(addDays(today, 2))) },
    { iso: toIso(addDays(today, daysUntilNextMon)), label: 'Next Monday' },
  ];
  // De-duplicate in case "Next Monday" collides with one of the earlier ones
  const seen = new Set();
  return items.filter((it) => {
    if (seen.has(it.iso)) return false;
    seen.add(it.iso);
    return true;
  });
};

const maxDateIso = () => toIso(addDays(new Date(), 60));

// ---------- UI helpers ----------

function Bubble({ from, children }) {
  return (
    <div className={`ks-msg ks-msg-${from}`}>
      <div className="ks-bubble">{children}</div>
    </div>
  );
}

function QuickReplies({ options, onPick }) {
  if (!options || options.length === 0) return null;
  return (
    <div className="ks-quick-replies">
      {options.map((o) => (
        <button
          key={o.value ?? o.label}
          type="button"
          className={`ks-chip${o.ghost ? ' ks-chip-ghost' : ''}`}
          onClick={() => onPick(o)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

export default function Chatbot() {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(STEPS.GREETING);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [form, setForm] = useState({
    name: '', phone: '',
    service_id: '', service_label: '',
    date: '', date_label: '',
    time: '', time_label: '',
  });
  const [services, setServices] = useState([]);
  const [servicesLoading, setServicesLoading] = useState(false);
  const [result, setResult] = useState(null);
  const scrollRef = useRef(null);

  // Boot: when the user first opens the chat, greet and load services
  useEffect(() => {
    if (!open || messages.length > 0) return;
    pushBot("🙏 Namaste! I'm the KayaSparsha assistant. I can book your appointment right here.");
    pushBot("What's your name?");
    setStep(STEPS.NAME);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load services when the user reaches the service step
  useEffect(() => {
    if (step !== STEPS.SERVICE || services.length > 0 || servicesLoading) return;
    setServicesLoading(true);
    fetch('/api/public/services')
      .then((r) => r.ok ? r.json() : Promise.reject(r))
      .then((data) => {
        const list = data.services || [];
        setServices(list);
      })
      .catch(() => {
        // Fall back to a small static list if the API is unreachable
        setServices([
          { service_id: 'consultation', service_name: 'Ayurvedic Consultation', category: 'consultation' },
          { service_id: 'abhyanga', service_name: 'Abhyanga Massage', category: 'panchakarma' },
          { service_id: 'shirodhara', service_name: 'Shirodhara', category: 'panchakarma' },
          { service_id: 'panchakarma', service_name: 'Panchakarma Programme', category: 'panchakarma' },
        ]);
      })
      .finally(() => setServicesLoading(false));
  }, [step, services.length, servicesLoading]);

  // Autoscroll on new messages
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open]);

  const pushBot = (text) => setMessages((m) => [...m, { from: 'bot', text }]);
  const pushUser = (text) => setMessages((m) => [...m, { from: 'user', text }]);

  const reset = () => {
    setMessages([]);
    setForm({
      name: '', phone: '',
      service_id: '', service_label: '',
      date: '', date_label: '',
      time: '', time_label: '',
    });
    setResult(null);
    setStep(STEPS.GREETING);
    setInput('');
  };

  const isValidPhone = (p) => /\d/.test(p) && p.replace(/\D/g, '').length >= 7;

  const onSubmitText = (e) => {
    e.preventDefault();
    const value = input.trim();
    if (!value) return;

    if (step === STEPS.NAME) {
      pushUser(value);
      setForm((f) => ({ ...f, name: value }));
      setInput('');
      pushBot(`Lovely to meet you, ${value}. 🌿`);
      pushBot("What's the best phone number to reach you on WhatsApp? (include country code, e.g. +91 98xxxxxxxx)");
      setStep(STEPS.PHONE);
      return;
    }

    if (step === STEPS.PHONE) {
      if (!isValidPhone(value)) {
        pushUser(value);
        setInput('');
        pushBot("That doesn't look like a valid phone. Could you share your number with country code?");
        return;
      }
      pushUser(value);
      setForm((f) => ({ ...f, phone: value }));
      setInput('');
      pushBot('Thanks! Which service would you like to book?');
      setStep(STEPS.SERVICE);
      return;
    }
  };

  const pickService = (svc) => {
    pushUser(svc.service_name);
    setForm((f) => ({
      ...f,
      service_id: svc.service_id,
      service_label: svc.service_name,
    }));
    pushBot('Lovely. Which day would suit you? Tap a quick pick below, or choose any date.');
    setStep(STEPS.DATE);
  };

  const pickDate = (iso, label) => {
    pushUser(label);
    const next = {
      ...form,
      date: iso || '',
      date_label: iso ? (label || formatDateLabel(iso)) : '',
    };
    setForm(next);
    if (iso) {
      pushBot('Great. What time works best for you?');
      setStep(STEPS.TIME);
    } else {
      // User skipped date — skip time too
      pushBot('No problem — our team will suggest a slot when they reach out.');
      goToConfirm({ ...next, time: '', time_label: '' });
    }
  };

  const pickTime = (value, label) => {
    pushUser(label);
    const next = {
      ...form,
      time: value || '',
      time_label: value ? label : '',
    };
    setForm(next);
    goToConfirm(next);
  };

  const skipDate = () => pickDate('', 'Any day that works');
  const skipTime = () => pickTime('', 'Any time');

  const goToConfirm = (f) => {
    setStep(STEPS.CONFIRM);
    const dateLine = f.date_label ? `\n• Date: ${f.date_label}` : '';
    const timeLine = f.time_label ? `\n• Time: ${f.time_label}` : '';
    pushBot(
      `Let me confirm:\n• Name: ${f.name}\n• Phone: ${f.phone}\n• Service: ${f.service_label}${dateLine}${timeLine}\n\nShall I go ahead and book it?`
    );
  };

  const submitBooking = async () => {
    pushUser('Yes, please book it');
    setStep(STEPS.SUBMITTING);
    pushBot('Booking your appointment…');

    try {
      const res = await fetch('/api/public/appointments', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name,
          phone: form.phone,
          service_id: form.service_id || undefined,
          service: form.service_label,
          date: form.date || undefined,
          time: form.time || undefined,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.message || 'Booking failed');
      }
      setResult(data);
      pushBot(`✅ Appointment request received (ID #${data.id}).`);
      pushBot(data.whatsapp_hint || 'Our team will reach out on WhatsApp or call shortly.');
      setStep(STEPS.DONE);
    } catch (err) {
      pushBot(`Sorry — I couldn't save the booking (${err.message}). Please try again or call us directly.`);
      setStep(STEPS.ERROR);
    }
  };

  const cancelBooking = () => {
    pushUser('Let me change something');
    pushBot("No worries — let's start over.");
    setTimeout(reset, 300);
  };

  // -------- Render --------

  if (!open) {
    return (
      <button
        type="button"
        className="ks-chat-launcher"
        onClick={() => setOpen(true)}
        aria-label="Open KayaSparsha assistant"
        title="Chat with KayaSparsha"
      >
        <span className="ks-chat-dot" />
        <span className="ks-chat-icon" aria-hidden="true">💬</span>
        <span className="ks-chat-launcher-label">Book on Chat</span>
      </button>
    );
  }

  const quickDates = step === STEPS.DATE ? getQuickDates() : [];

  return (
    <div className="ks-chat" role="dialog" aria-label="KayaSparsha assistant">
      <div className="ks-chat-header">
        <div className="ks-chat-avatar" aria-hidden="true">🌿</div>
        <div className="ks-chat-title">
          <strong>KayaSparsha Assistant</strong>
          <small>Typically replies instantly</small>
        </div>
        <button
          type="button"
          className="ks-chat-close"
          onClick={() => setOpen(false)}
          aria-label="Close chat"
        >
          ×
        </button>
      </div>

      <div className="ks-chat-body" ref={scrollRef}>
        {messages.map((m, i) => (
          <Bubble key={i} from={m.from}>{m.text}</Bubble>
        ))}

        {step === STEPS.SERVICE && (
          <QuickReplies
            options={
              servicesLoading
                ? [{ label: 'Loading services…', value: '__loading__' }]
                : services.slice(0, 10).map((s) => ({
                    label: s.service_name,
                    value: s.service_id,
                    svc: s,
                  }))
            }
            onPick={(o) => {
              if (o.value === '__loading__') return;
              pickService(o.svc);
            }}
          />
        )}

        {step === STEPS.DATE && (
          <div className="ks-date-picker">
            <div className="ks-quick-replies">
              {quickDates.map((d) => (
                <button
                  key={d.iso}
                  type="button"
                  className="ks-chip"
                  onClick={() => pickDate(d.iso, d.label)}
                >
                  {d.label}
                </button>
              ))}
              <button
                type="button"
                className="ks-chip ks-chip-ghost"
                onClick={skipDate}
              >
                Any day
              </button>
            </div>
            <label className="ks-date-custom">
              <span>Or pick an exact date:</span>
              <input
                type="date"
                min={todayIso()}
                max={maxDateIso()}
                value={form.date || ''}
                onChange={(e) => {
                  const iso = e.target.value;
                  if (!iso) return;
                  pickDate(iso, formatDateLabel(iso));
                }}
              />
            </label>
          </div>
        )}

        {step === STEPS.TIME && (
          <div className="ks-quick-replies">
            {TIME_SLOTS.map((t) => (
              <button
                key={t.value}
                type="button"
                className="ks-chip"
                onClick={() => pickTime(t.value, t.label)}
              >
                {t.label}
              </button>
            ))}
            <button
              type="button"
              className="ks-chip ks-chip-ghost"
              onClick={skipTime}
            >
              Any time
            </button>
          </div>
        )}

        {step === STEPS.CONFIRM && (
          <QuickReplies
            options={[
              { label: '✅ Confirm booking', value: 'yes' },
              { label: '↺ Change details', value: 'no', ghost: true },
            ]}
            onPick={(o) => (o.value === 'yes' ? submitBooking() : cancelBooking())}
          />
        )}

        {step === STEPS.DONE && (
          <QuickReplies
            options={[{ label: 'Book another', value: 'restart' }]}
            onPick={reset}
          />
        )}
      </div>

      {[STEPS.NAME, STEPS.PHONE].includes(step) && (
        <form className="ks-chat-input" onSubmit={onSubmitText}>
          <input
            type={step === STEPS.PHONE ? 'tel' : 'text'}
            placeholder={
              step === STEPS.NAME ? 'Your name…' :
              step === STEPS.PHONE ? '+91 98xxxxxxxx' :
              'Type here…'
            }
            value={input}
            onChange={(e) => setInput(e.target.value)}
            autoFocus
          />
          <button type="submit" aria-label="Send">➤</button>
        </form>
      )}

      <div className="ks-chat-footer">
        <span>Powered by KayaSparsha</span>
      </div>
    </div>
  );
}
